import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { BRAND } from "../lib/brand";

/**
 * Auto-advancing hero carousel — no in-slide CTAs.
 * (Get a Quote remains accessible from the header.)
 */
export default function HeroCarousel() {
  const slides = BRAND.heroSlides;
  const [index, setIndex] = React.useState(0);
  const timerRef = React.useRef(null);

  const goTo = React.useCallback(
    (i) => setIndex((i + slides.length) % slides.length),
    [slides.length]
  );
  const next = React.useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = React.useCallback(() => goTo(index - 1), [index, goTo]);

  React.useEffect(() => {
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 5500);
    return () => clearInterval(timerRef.current);
  }, [slides.length, index]);

  return (
    <section
      data-testid="hero-carousel"
      className="relative overflow-hidden bg-wood-900"
    >
      <div className="relative h-[70vh] min-h-[460px] max-h-[860px] sm:h-[80vh] sm:min-h-[540px] w-full">
        {slides.map((s, i) => (
          <div
            key={i}
            data-testid={`hero-slide-${i}`}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              i === index ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <div className="absolute inset-0 bg-wood-900" />
            <img
              src={s.img}
              alt={s.title}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: s.objectPosition || "center" }}
            />
            {/* Gradient overlay for readable text */}
            <div className="absolute inset-0 bg-gradient-to-r from-wood-900/85 via-wood-900/55 to-wood-900/10" />

            <div className="relative h-full mx-auto max-w-7xl px-6 sm:px-8 flex items-center">
              <div
                className={`max-w-2xl transition-all duration-700 ${
                  i === index ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
              >
                <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-400 font-bold">
                  {s.eyebrow}
                </div>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-wood-50 leading-[1.05] mt-4">
                  {s.title}
                </h1>
                <p className="mt-5 text-base sm:text-lg text-wood-100/85 max-w-xl leading-relaxed">
                  {s.caption}
                </p>
              </div>
            </div>
          </div>
        ))}

        <button
          data-testid="hero-prev-btn"
          onClick={prev}
          aria-label="Previous slide"
          className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-white/10 backdrop-blur text-white hover:bg-gold-500 hover:text-wood-900 transition-all items-center justify-center"
        >
          <ChevronLeft size={22} />
        </button>
        <button
          data-testid="hero-next-btn"
          onClick={next}
          aria-label="Next slide"
          className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full bg-white/10 backdrop-blur text-white hover:bg-gold-500 hover:text-wood-900 transition-all items-center justify-center"
        >
          <ChevronRight size={22} />
        </button>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {slides.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-dot-${i}`}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-10 bg-gold-400" : "w-5 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
